import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck, Clock } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try { setNotes((await api.get("/notifications")).data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = notes.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
  };
  const openNote = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setNotes((ns) => ns.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.task_id) {
      setOpen(false);
      navigate(`/app/tasks?task=${n.task_id}`);
    }
  };
  const snooze = async (e, n, hours) => {
    e.stopPropagation();
    try {
      await api.post(`/tasks/${n.task_id}/snooze`, { hours, member_id: n.member_id });
      toast.success(`Reminder snoozed for ${hours < 1 ? `${hours * 60}m` : `${hours}h`}`);
      if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
      setNotes((ns) => ns.map((x) => x.id === n.id ? { ...x, read: true } : x));
    } catch { toast.error("Could not snooze"); }
  };
  const cancelScheduled = async (e, n) => {
    e.stopPropagation();
    try {
      await api.post(`/scheduled-emails/${n.scheduled_id}/cancel`);
      toast.success("Email cancelled before sending");
      setNotes((ns) => ns.map((x) => x.id === n.id ? { ...x, read: true, cancelable: false, text: "🚫 Cancelled: " + (x.order_code ? `Order ${x.order_code}` : "Automated email") } : x));
    } catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors">
        <Bell className="w-[18px] h-[18px]" />
        {unread > 0 && (
          <span data-testid="notification-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          className="absolute left-0 mt-2 w-[24rem] max-w-[calc(100vw-2rem)] max-h-[28rem] overflow-y-auto rounded-2xl border border-slate-600 bg-[#111a2b] shadow-2xl z-[60]">
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-700 sticky top-0 bg-[#111a2b]">
            <p className="text-base font-semibold text-white">Notifications</p>
            {unread > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAll}
                className="text-xs font-medium text-emerald-300 hover:text-emerald-200 flex items-center gap-1">
                <CheckCheck className="w-4 h-4" /> Mark all read
              </button>
            )}
          </div>
          <div className="divide-y divide-slate-700/60">
            {notes.length === 0 && <p className="text-center text-slate-400 text-sm py-10">No notifications yet.</p>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`notification-${n.id}`} onClick={() => openNote(n)}
                className={`w-full text-left px-4 py-3.5 hover:bg-slate-700/40 transition-colors flex gap-3 cursor-pointer ${n.read ? "opacity-70" : "bg-slate-800/30"}`}>
                {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />}
                <div className={n.read ? "pl-5 min-w-0" : "min-w-0"}>
                  <p className="text-[13px] text-slate-100 leading-relaxed break-words whitespace-normal">{n.text}</p>
                  <p className="text-xs text-slate-400 mt-1">{relTime(n.created_at)}</p>
                  {n.type === "due_soon" && n.task_id && (
                    <div className="flex items-center gap-1.5 mt-2" data-testid={`snooze-row-${n.id}`}>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Snooze:</span>
                      {[["1h", 1], ["3h", 3], ["Tomorrow", 24]].map(([lbl, hrs]) => (
                        <button key={lbl} data-testid={`snooze-${n.id}-${hrs}`} onClick={(e) => snooze(e, n, hrs)}
                          className="text-[10px] text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5 hover:bg-emerald-500/10">{lbl}</button>
                      ))}
                    </div>
                  )}
                  {n.type === "email_scheduled" && n.cancelable && n.scheduled_id && (
                    <div className="flex items-center gap-1.5 mt-2" data-testid={`sched-cancel-row-${n.id}`}>
                      <button data-testid={`notification-cancel-${n.scheduled_id}`} onClick={(e) => cancelScheduled(e, n)}
                        className="text-[11px] font-semibold text-rose-300 border border-rose-500/40 rounded-full px-2.5 py-0.5 hover:bg-rose-500/10">
                        Cancel this email
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}